import { convertFileToBase64 } from "./convertFileToBase64";

type ImportOptions = {
  service: (payload: Record<string, unknown>) => Promise<any>;
  payloadExtras?: Record<string, unknown>;
  onRefresh?: () => void;
  asyncMessage?: string;

  // UI state setters (coming from screen)
  setIsImporting: (value: boolean) => void;
  setImportMessage: (msg: string | null) => void;
  setImportResults: (results: any) => void;
  setOpenImportResultsDialog: (value: boolean) => void;
  setOpenImportInventoryDialog: (value: boolean) => void;
};

export const handleImportData = async (
  formData: Record<string, unknown>,
  {
    service,
    payloadExtras = {},
    onRefresh,
    asyncMessage = "Import started successfully. You will be notified when it completes.",
    setIsImporting,
    setImportMessage,
    setImportResults,
    setOpenImportResultsDialog,
    setOpenImportInventoryDialog,
  }: ImportOptions
) => {
  const { file } = formData;
  if (file && file instanceof File) {
    setIsImporting(true);
    setImportMessage(null);

    try {
      // Convert file to base64
      const base64Data = await convertFileToBase64(file);

      // Build payload
      const payload = { file: base64Data, ...payloadExtras };

      // Call API service
      const response = await service(payload);
      const responseData = (response as any)?.data;

      // Case 1: API returns detailed results
      if (
        responseData?.data?.invalidData || // facility-style
        responseData?.summary || // general-style
        responseData?.valid ||
        responseData?.invalid
      ) {
        const transformedResults = responseData?.data?.invalidData
          ? {
              summary: {
                totalRecords: responseData.data.totalRows || 0,
                validRecords: responseData.data.validRecords || 0,
                invalidRecords: responseData.data.invalidRecords || 0,
                createdInventory: responseData.data.createdRecords || 0,
                totalProcessed: responseData.data.totalRows || 0,
              },
              valid: [],
              invalid: responseData.data.invalidData.map(
                (item: Record<string, unknown>, index: number) => ({
                  index: index + 1,
                  reason: item.reason || "Unknown error",
                  row: item.row || {},
                })
              ),
              message: responseData.message || "Import completed",
            }
          : responseData;

        setImportResults(transformedResults);
        setOpenImportResultsDialog(true);
        setOpenImportInventoryDialog(false);
      }
      // Case 2: Async or simple success
      else if (responseData?.success) {
        setImportMessage(responseData.message || asyncMessage);
        setOpenImportInventoryDialog(false);
      }
      // Case 3: Error
      else {
        setImportMessage(
          responseData?.message ||
            "Failed to import inventory. Please try again."
        );
      }

      // Refresh inventory list
      if (onRefresh) {
        setTimeout(onRefresh, 1000);
      }
    } catch (error) {
      console.error("Error importing data:", error);
      setImportMessage(
        "An error occurred while importing inventory. Please try again."
      );
    } finally {
      setIsImporting(false);
    }
  }
};
